import type { BranchSettingsView } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchBranches, fetchGridPreview, saveBranch } from "../../api/settings.api"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Button } from "../../ui"
import { StandHeader } from "../directories/stand"
import {
  branchUpsertRequest,
  conflictText,
  DEFAULT_STEP,
  GRID_STEPS,
  type GridStep,
  offGridWarning,
  PACKING_TEXT,
  STEP_TEXT,
  stepLabel,
} from "./branch-grid-model"
import styles from "./branch-screen.module.css"

/**
 * 4-Б1 «Филиал: шаг сетки и режим заполнения» — /branches/:id.
 *
 * ДВЕ НАСТРОЙКИ, КОТОРЫЕ МЕНЯЮТ ВЕСЬ ДЕНЬ САЛОНА. Шаг решает, какое время увидит клиент
 * на витрине; режим заполнения — будет ли это время круглым. Обе подписаны последствием
 * из макета, а не названием поля: «15 минут» владельцу не говорит ничего, «клиенту
 * предлагается 10:00, 10:15, 10:30» говорит всё.
 *
 * ЧИСЛО ЗАПИСЕЙ ВНЕ НОВОЙ СЕТКИ СПРАШИВАЕТСЯ ДО СОХРАНЕНИЯ. Смена шага записи не двигает:
 * визит на 14:15 при шаге 30 остаётся на 14:15 и рисуется между линиями. Это ожидаемо,
 * но выглядит поломкой — и, узнав об этом после сохранения, владелец звонит в поддержку.
 *
 * СОХРАНЯЕТСЯ ФИЛИАЛ ЦЕЛИКОМ, а не два видимых поля: это upsert, и пропущенное он
 * обнулит. Ровно так и пропадали координаты с телефоном, которых экран даже не рисует.
 */
export function BranchScreen(props: {
  readonly branchId: string
  readonly onBack: () => void
}): ReactNode {
  const canManage = useHasPermission("branches.manage")

  const [branch, setBranch] = useState<BranchSettingsView | null>(null)
  const [loadFailed, setLoadFailed] = useState<ApiProblem | null>(null)
  /*
   * «НЕ НАЙДЕН» — ОТДЕЛЬНОЕ СОСТОЯНИЕ, А НЕ ВЕЧНАЯ ЗАГРУЗКА. Список филиалов сервер
   * фильтрует по доступу, и филиала в нём не будет у того, чья область сужена до другой
   * точки, — а ссылку «поставь тут шаг 30» владелец шлёт управляющему именно так.
   * Пока это состояние сливалось с загрузкой, человек смотрел на три серых полосы
   * без единого слова и решал, что кабинет завис.
   */
  const [missing, setMissing] = useState(false)
  const [step, setStep] = useState<GridStep>(DEFAULT_STEP)
  const [packing, setPacking] = useState<"grid" | "tight">("grid")
  const [offGrid, setOffGrid] = useState<number | "unknown" | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [conflict, setConflict] = useState<BranchSettingsView | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  /**
   * `keepForm` — ПЕРЕЧИТАТЬ, НО НЕ ТРОГАТЬ ВЫБОР ЧЕЛОВЕКА.
   *
   * В разборе конфликта версий перечитывание затирало поля серверными значениями ДО
   * того, как показать конфликт, — и кнопка «Оставить моё» сохраняла ЧУЖОЕ: владелец
   * ставил 30, управляющий успевал поставить 60, экран подставлял 60 в оба места и
   * сообщал «сохранено». Выбор исчезал молча, а текст конфликта называл одно и то же
   * число дважды: «Шаг сейчас 60, вы ставите 60».
   */
  const load = useCallback(
    async (options?: {
      readonly signal?: AbortSignal
      readonly keepForm?: boolean
    }): Promise<BranchSettingsView | null> => {
      const signal = options?.signal
      try {
        const all = await fetchBranches(signal)
        const found = all.find((item) => item.id === props.branchId) ?? null
        if (signal?.aborted === true) return null
        setBranch(found)
        setLoadFailed(null)
        setMissing(found === null)
        if (found !== null && options?.keepForm !== true) {
          setStep((found.slotStepMinutes as GridStep) ?? DEFAULT_STEP)
          setPacking(found.packingMode)
        }
        return found
      } catch (error) {
        if (signal?.aborted === true) return null
        if (!isApiProblem(error)) throw error
        setLoadFailed(error)
        return null
      }
    },
    [props.branchId],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load({ signal: controller.signal })
    return () => controller.abort()
  }, [load])

  /*
   * Предпросмотр спрашивается на КАЖДЫЙ выбранный шаг, но только когда шаг отличается
   * от сохранённого: спрашивать «сколько записей вне сетки» про текущую сетку незачем —
   * ответ всегда ноль, а запрос уходит на каждое открытие экрана.
   */
  useEffect(() => {
    if (branch === null || step === branch.slotStepMinutes) {
      setOffGrid(null)
      return
    }
    // Прежнее число гасится СРАЗУ: пока летит запрос про новый шаг, на экране стояло
    // число от предыдущего — подпись под чипом уже про 60, а предупреждение всё ещё
    // про 15. На планшете в салоне это окно в секунды, и нажать «Сохранить» в нём легко.
    setOffGrid(null)
    const controller = new AbortController()
    void (async () => {
      try {
        const preview = await fetchGridPreview(branch.id, step, controller.signal)
        if (!controller.signal.aborted) setOffGrid(preview.offGrid)
      } catch {
        // Отказ предпросмотра — это «неизвестно», а не «ноль»: молчание читается
        // как «всё ровно», и владелец сохранит шаг, ничего не зная.
        if (!controller.signal.aborted) setOffGrid("unknown")
      }
    })()
    return () => controller.abort()
  }, [branch, step])

  if (loadFailed !== null && branch === null) {
    return <ProblemScreen problem={loadFailed} onRetry={() => void load()} />
  }

  const dirty =
    branch !== null && (step !== branch.slotStepMinutes || packing !== branch.packingMode)

  const warning =
    branch === null || offGrid === null
      ? null
      : offGridWarning({ currentStep: branch.slotStepMinutes, nextStep: step, offGrid })

  const save = async (force?: BranchSettingsView): Promise<void> => {
    const base = force ?? branch
    if (base === null) return
    setBusy(true)
    setProblem(null)
    try {
      await saveBranch(branchUpsertRequest(base, { slotStepMinutes: step, packingMode: packing }))
      setConflict(null)
      setSaved(true)
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      if (error.code === "version_conflict") {
        // Перечитываем и показываем ОБА значения: решать, чьё оставить, человек может
        // только видя их рядом. «Обновите страницу» отправляет его начинать сначала.
        // keepForm — выбор человека остаётся в полях, иначе «Оставить моё» сохранит чужое.
        const fresh = await load({ keepForm: true })
        if (fresh !== null) setConflict(fresh)
        return
      }
      setProblem(error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles["screen"]}>
      <StandHeader title={branch?.name ?? "Филиал"}>
        <Button size="sm" variant="secondary" onClick={props.onBack}>
          Все филиалы
        </Button>
      </StandHeader>

      {missing ? (
        <div className={styles["column"]}>
          <div className={styles["error"]} role="alert">
            <b>Филиал не найден.</b> Либо его выключили, либо он не в вашей области:
            владелец мог сузить её до другой точки. Ссылка при этом выглядит рабочей —
            проверьте её у того, кто прислал, или откройте список филиалов.
          </div>
        </div>
      ) : branch === null ? (
        <div className={styles["skeletons"]}>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} width="100%" height={48} radius={10} />
          ))}
        </div>
      ) : (
        <div className={styles["column"]}>
          <p className={styles["hint"]}>Часы работы и адрес — в справочнике филиалов.</p>

          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Шаг сетки расписания</div>
            <div className={styles["chips"]}>
              {GRID_STEPS.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  variant={value === step ? "primary" : "secondary"}
                  deniedReason={canManage ? undefined : "Настройки филиала меняет владелец"}
                  onClick={() => {
                    setStep(value)
                    setSaved(false)
                  }}
                >
                  {stepLabel(value)}
                </Button>
              ))}
            </div>
            <p className={styles["consequence"]}>{STEP_TEXT[step]}</p>
            {warning === null ? null : (
              <div className={styles["warning"]} role="status">
                {warning}
              </div>
            )}
          </div>

          <div className={styles["block"]}>
            <div className={styles["blockTitle"]}>Режим заполнения дня</div>
            <div className={styles["modes"]}>
              {(["grid", "tight"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={packing === mode ? styles["modeActive"] : styles["mode"]}
                  disabled={!canManage}
                  onClick={() => {
                    setPacking(mode)
                    setSaved(false)
                  }}
                >
                  <span className={styles["modeTitle"]}>{PACKING_TEXT[mode].title}</span>
                  <span className={styles["modeText"]}>{PACKING_TEXT[mode].text}</span>
                </button>
              ))}
            </div>
          </div>

          {conflict === null ? null : (
            <div className={styles["conflict"]} role="alert">
              <span>{conflictText({ currentStep: conflict.slotStepMinutes, wantedStep: step })}</span>
              <div className={styles["conflictButtons"]}>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    void save(conflict)
                  }}
                >
                  Оставить моё
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setStep(conflict.slotStepMinutes as GridStep)
                    setPacking(conflict.packingMode)
                    setConflict(null)
                  }}
                >
                  Взять текущее
                </Button>
              </div>
            </div>
          )}

          {problem === null ? null : (
            <div className={styles["error"]} role="alert">
              <b>{problem.title}</b> {problem.detail ?? ""}
            </div>
          )}
          {saved ? (
            <div className={styles["saved"]} role="status">
              Настройки филиала сохранены.
            </div>
          ) : null}

          <div className={styles["footer"]}>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Сохраняем…"
              deniedReason={
                !canManage
                  ? "Настройки филиала меняет владелец или управляющий — вы видите их только на чтение"
                  : dirty
                    ? undefined
                    : "Менять нечего"
              }
              onClick={() => {
                void save()
              }}
            >
              Сохранить
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
